/**
 * captureViewerThumbnail.ts — Screenshot des Potree WebGL-Canvas
 *
 * WICHTIG: Three.js WebGLRenderer hat preserveDrawingBuffer intern auf false.
 * Daher muss vor toDataURL ein Frame gerendert werden. Wir rufen
 * viewer.render() (Potree's eigene EDL/Splat-Pipeline) auf, nicht
 * renderer.render(scene, camera).
 */

/** Einzelnes Thumbnail in beliebiger Aufloesung */
export function captureViewerThumbnail(opts?: {
  quality?: number
  maxThumbSize?: number
}): string | null {
  const { quality = 0.7, maxThumbSize = 320 } = opts || {}

  try {
    const v = (window as any).viewer
    if (!v?.renderer) return null

    const canvas: HTMLCanvasElement | undefined = v.renderer.domElement
    if (!canvas || canvas.width === 0 || canvas.height === 0) return null

    // Potree-eigenen Render-Frame erzwingen
    _forceRender(v)

    return _resizeAndExport(canvas, maxThumbSize, quality)
  } catch {
    return null
  }
}

/** Zwei Thumbnails (small + medium) in einem Render-Pass */
export function captureViewerThumbnailPair(opts?: {
  quality?: number
  smallSize?: number
  mediumSize?: number
}): { small: string; medium: string } | null {
  const { quality = 0.7, smallSize = 88, mediumSize = 320 } = opts || {}

  try {
    const v = (window as any).viewer
    if (!v?.renderer) return null

    const canvas: HTMLCanvasElement | undefined = v.renderer.domElement
    if (!canvas || canvas.width === 0 || canvas.height === 0) return null

    // Einmal rendern, dann beide Groessen erzeugen
    _forceRender(v)

    const small = _resizeAndExport(canvas, smallSize, quality)
    const medium = _resizeAndExport(canvas, mediumSize, quality)

    if (!small || !medium) return null
    return { small, medium }
  } catch {
    return null
  }
}

// ── Interne Helfer ──────────────────────────────────────────────────────────

function _forceRender(viewer: any): void {
  try {
    if (typeof viewer.render === 'function') {
      viewer.render()
    } else if (typeof viewer.renderDefault === 'function') {
      viewer.renderDefault()
    }
  } catch {
    // Stilles Scheitern — trotzdem versuchen, den Buffer zu lesen
  }
}

function _resizeAndExport(
  canvas: HTMLCanvasElement,
  maxSize: number,
  quality: number,
): string | null {
  try {
    if (maxSize && (canvas.width > maxSize || canvas.height > maxSize)) {
      const aspect = canvas.width / canvas.height
      let tw: number, th: number
      if (aspect >= 1) {
        tw = maxSize
        th = Math.round(maxSize / aspect)
      } else {
        th = maxSize
        tw = Math.round(maxSize * aspect)
      }
      const offscreen = document.createElement('canvas')
      offscreen.width = tw
      offscreen.height = th
      const ctx = offscreen.getContext('2d')
      if (!ctx) return canvas.toDataURL('image/jpeg', quality)
      ctx.drawImage(canvas, 0, 0, tw, th)
      return offscreen.toDataURL('image/jpeg', quality)
    }
    return canvas.toDataURL('image/jpeg', quality)
  } catch {
    return null
  }
}

/** Medium + High Thumbnails (320px + 640px) in einem Render-Pass */
export function captureViewerThumbnailMediumHigh(opts?: {
  quality?: number
  mediumSize?: number
  highSize?: number
}): { medium: string; high: string } | null {
  const { quality = 0.75, mediumSize = 320, highSize = 640 } = opts || {}

  try {
    const v = (window as any).viewer
    if (!v?.renderer) return null

    const canvas: HTMLCanvasElement | undefined = v.renderer.domElement
    if (!canvas || canvas.width === 0 || canvas.height === 0) return null

    _forceRender(v)

    const medium = _resizeAndExport(canvas, mediumSize, quality)
    const high = _resizeAndExport(canvas, highSize, quality)

    if (!medium || !high) return null
    return { medium, high }
  } catch {
    return null
  }
}

// Globale Funktion fuer script.js
;(window as any).captureViewerThumbnail = captureViewerThumbnail
;(window as any).captureViewerThumbnailPair = captureViewerThumbnailPair
;(window as any).captureViewerThumbnailMediumHigh = captureViewerThumbnailMediumHigh
